import {
  CopyPlus,
  Edit2,
  Logs,
  Repeat2,
  StickyNote,
  Trash2,
  ChevronRight,
} from "lucide-react";
import SimpleListItem from "@/components/foundations/lists/SimpleListItem/SimpleListItem";
import { countTaskLogLines } from "@/components/tasks/details/TaskLogEditor/TaskLogEditor";
import {
  repeatSummary,
  type RepeatRule,
} from "@/components/tasks/repeat/RepeatOptions/repeat-options-types";
import type { TaskVisibleFields } from "@shared/taskVisibleFields";
import OptionsWrapper from "../OptionsWrapper/OptionsWrapper";

type Task = {
  title: string;
  details?: string | null;
  log?: string | null;
  listName?: string | null;
  barColor?: string | null;
  bgColor?: string | null;
};
type Colors = { bar: string; soft: string; ink: string };
type Props = {
  task: Task;
  hasNotes: boolean;
  colors: Colors;
  visibleFields: TaskVisibleFields;
  onToggleVisibility: (field: keyof TaskVisibleFields) => void;
  onAddNote: () => void;
  onOpenLog: () => void;
  onOpenRepeat: () => void;
  repeatRule: RepeatRule;
  onOpenDisplay: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onBack: () => void;
  onEditPattern?: () => void;
  onConvertToPattern?: () => void;
};

function SectionLabel({
  children,
  color,
}: {
  children: string;
  color: string;
}) {
  return (
    <div
      className="ui-options-section__label px-1 text-[11px] font-semibold uppercase tracking-wider"
      style={{ color, opacity: 0.55 }}
    >
      {children}
    </div>
  );
}

export default function TaskOptionsView({
  task,
  hasNotes,
  colors,
  visibleFields,
  onToggleVisibility,
  onAddNote,
  onOpenLog,
  onOpenRepeat,
  repeatRule,
  onOpenDisplay,
  onEdit,
  onDuplicate,
  onDelete,
  onBack,
  onEditPattern,
  onConvertToPattern,
}: Props) {
  const colorStatusLabel =
    task.barColor != null || task.bgColor != null
      ? "Custom colors"
      : "Using list colors";
  return (
    <OptionsWrapper onBack={onBack} accentColor={colors.ink}>
      <section className="ui-options-section space-y-2">
        <SectionLabel color={colors.ink}>Display</SectionLabel>
        <button
          type="button"
          onClick={onOpenDisplay}
          className="ui-options-display-item w-full overflow-hidden rounded-xl border text-left transition-opacity hover:opacity-80 active:opacity-60"
          style={{
            background: colors.soft,
            borderColor: `color-mix(in oklch, ${colors.bar} 28%, transparent)`,
          }}
        >
          <div className="flex items-stretch">
            <span
              className="w-1.5 shrink-0"
              style={{ background: colors.bar }}
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1 px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <span
                  className="truncate text-base font-bold leading-snug"
                  style={{ color: colors.ink }}
                >
                  {task.title}
                </span>
                {task.listName && (
                  <span
                    className="max-w-32 shrink-0 truncate rounded-full px-2 py-0.5 text-[11px] uppercase tracking-[0.14em]"
                    style={{
                      background: `color-mix(in oklch, ${colors.bar} 18%, transparent)`,
                      color: colors.ink,
                    }}
                  >
                    {task.listName}
                  </span>
                )}
              </div>
              {task.details && (
                <div
                  className="mt-0.5 truncate text-[15px] font-semibold"
                  style={{ color: colors.ink, opacity: 0.75 }}
                >
                  {task.details}
                </div>
              )}
            </div>
            <div className="flex items-center pr-3">
              <ChevronRight
                className="h-4 w-4"
                style={{ color: colors.ink, opacity: 0.5 }}
                aria-hidden="true"
              />
            </div>
          </div>
        </button>
        <div
          className="px-1 pb-1 text-xs"
          style={{ color: colors.ink, opacity: 0.7 }}
        >
          {colorStatusLabel}
        </div>
      </section>
      <section className="ui-options-section space-y-2">
        <SectionLabel color={colors.ink}>Visibility</SectionLabel>
        <div className="space-y-2">
          <div className="overflow-hidden rounded-xl border">
            <SimpleListItem
              icon={<Repeat2 className="h-4 w-4" />}
              title={`Repeat: ${repeatSummary(repeatRule)}`}
              leftIndicator={visibleFields.repeat ? "checked" : "empty"}
              rightIndicator="chevron"
              onClick={onOpenRepeat}
              onLeftIndicatorClick={() => onToggleVisibility("repeat")}
              onRightIndicatorClick={onOpenRepeat}
            />
          </div>
          <div className="overflow-hidden rounded-xl border">
            <SimpleListItem
              icon={<StickyNote className="h-4 w-4" />}
              title={hasNotes ? "Edit note" : "Add note"}
              leftIndicator={visibleFields.notes ? "checked" : "empty"}
              rightIndicator="chevron"
              onClick={onAddNote}
              onLeftIndicatorClick={() => onToggleVisibility("notes")}
              onRightIndicatorClick={onAddNote}
            />
          </div>
          <div className="overflow-hidden rounded-xl border">
            <SimpleListItem
              icon={<Logs className="h-4 w-4" />}
              title={`Event log (${countTaskLogLines(task.log)})`}
              leftIndicator={visibleFields.log ? "checked" : "empty"}
              rightIndicator="chevron"
              onClick={onOpenLog}
              onLeftIndicatorClick={() => onToggleVisibility("log")}
              onRightIndicatorClick={onOpenLog}
            />
          </div>
        </div>
      </section>
      <section className="ui-options-section space-y-2">
        <SectionLabel color={colors.ink}>Actions</SectionLabel>
        {onEditPattern && (
          <button
            type="button"
            onClick={onEditPattern}
            className="ui-options-action flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-sm transition-colors hover:bg-accent/20"
          >
            <Edit2
              className="h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
            <span>Edit Pattern</span>
          </button>
        )}
        {onConvertToPattern && (
          <button
            type="button"
            onClick={onConvertToPattern}
            className="ui-options-action flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-sm transition-colors hover:bg-accent/20"
          >
            <CopyPlus
              className="h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
            <span>Convert to Pattern</span>
          </button>
        )}
        <button
          type="button"
          onClick={onEdit}
          className="ui-options-action flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-sm transition-colors hover:bg-accent/20"
        >
          <Edit2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <span>Edit</span>
        </button>
        <button
          type="button"
          onClick={onDuplicate}
          className="ui-options-action flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-sm transition-colors hover:bg-accent/20"
        >
          <CopyPlus
            className="h-4 w-4 text-muted-foreground"
            aria-hidden="true"
          />
          <span>Duplicate</span>
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="ui-options-action flex w-full items-center gap-3 rounded-xl border border-destructive/30 px-4 py-3 text-sm text-destructive transition-colors hover:bg-destructive/10"
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
          <span>Delete</span>
        </button>
      </section>
    </OptionsWrapper>
  );
}
